import { Button } from "@kalyzee/kalyzee-ui";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  getSettingsConfigResolvedData,
  getSettingsConfigSource,
  getSettingsConfigVariables,
  initSettings,
  resolveActiveSettingsKey,
  setSettingsConfigSource,
  setSettingsConfigVariablesUrl,
  SettingsInitResult,
} from "../../helpers/settings";
import { toastWarning } from "../../helpers/toast";
import { AnimatedBackground } from "./AnimatedBackground";
import { LoadingView } from "./LoadingView";
import styles from "./SettingsBootGate.module.css";

/**
 * Seconds before an unattended screen (slave, kiosk) starts on the cache anyway.
 * Deliberately short: the wrapper reloads a page that stays silent for too long.
 */
const AUTO_CONTINUE_DELAY = 5;

/**
 * Hard cap on how long the app may be held back. The wrapper (Electron room
 * app) reloads the page when it does not receive the app's `ready` message, so
 * a gate that waits for a slow URL turns into a reload loop: past this delay we
 * mount on the cache and let the fetch land in the background.
 */
const MAX_BLOCK_MS = 1500;

/**
 * Boot gate for a remote settings config: the app waits for the URL just long
 * enough that a nearby file (the usual case) is applied before anything renders
 * — never longer, because the wrapper reloads a page that stays silent, which
 * would turn a slow URL into a reload loop.
 *
 * Past that cap the app is mounted and the fetch keeps going; a failure is then
 * shown OVER the running app, so the report is the same whether the URL fails
 * in 10 ms (connection refused) or in 8 s (unreachable host). Only undefined
 * variables take the lighter route of a notification: the config did load.
 */
export const SettingsBootGate = ({ children }: { children: React.ReactNode }) => {
  // A local config resolves synchronously — no loading screen flash for it,
  // unless it has unresolved variables, which `initSettings` reports.
  const [result, setResult] = useState<SettingsInitResult | undefined>(() => {
    const key = resolveActiveSettingsKey();
    const source = getSettingsConfigSource(key);
    if (source.mode === "remote" || source.variablesMode === "remote") return undefined;
    if (getSettingsConfigResolvedData(key).missing.length) return undefined;
    return { key, mode: "local", hasCache: true };
  });
  const [dismissed, setDismissed] = useState(false);
  // Set once the app is mounted: from then on nothing may unmount it, whatever
  // the pending load ends up reporting.
  const [proceeded, setProceeded] = useState(false);
  const [countdown, setCountdown] = useState(AUTO_CONTINUE_DELAY);
  // StrictMode double-mount would otherwise fire two loads.
  const loadingRef = useRef(false);
  // Whether the failure was already reported (screen or notification).
  const reportedRef = useRef(false);

  const load = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setResult(undefined);
    const settle = (res: SettingsInitResult) => {
      loadingRef.current = false;
      setCountdown(AUTO_CONTINUE_DELAY);
      setResult(res);
    };
    // Nothing here may leave the app on the loading screen: an unexpected
    // failure is reported like any other, and the app still starts.
    initSettings().then(settle, (err) => settle({
      key: resolveActiveSettingsKey(),
      mode: "local",
      error: `Erreur inattendue au chargement de la configuration :\n${(err as Error)?.message ?? String(err)}`,
      errorKind: "fetch",
      hasCache: true,
    }));
  }, []);

  useEffect(() => {
    if (!result) load();
    // Boot only: later loads go through the retry button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A pending load never holds the app back for long: past the cap it starts on
  // whatever is cached (nothing, at worst — the app then runs on its defaults)
  // and the fetch applies when it lands.
  useEffect(() => {
    if (result || proceeded) return;
    const timer = setTimeout(() => setProceeded(true), MAX_BLOCK_MS);
    return () => clearTimeout(timer);
  }, [result, proceeded]);

  const blocked = !!result?.error && result?.errorKind !== "variables" && !dismissed;

  useEffect(() => {
    if (!blocked) return;
    const timer = setInterval(() => setCountdown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [blocked]);

  useEffect(() => {
    if (blocked) reportedRef.current = true; // the screen below says it all
  }, [blocked]);

  useEffect(() => {
    if (blocked && countdown <= 0) setDismissed(true);
  }, [blocked, countdown]);

  /**
   * Undefined variables are the one failure that does not deserve the screen:
   * the config itself loaded, and a partially configured room would be covered
   * at every start. They are raised as a notification that stays until closed.
   */
  useEffect(() => {
    if (result?.errorKind !== "variables" || reportedRef.current) return;
    reportedRef.current = true;
    const message = `Configuration incomplète : ${result.error?.replace(/\n[\s\S]*/, "")}`;
    // Delayed: the toast container belongs to the app tree being mounted.
    const timer = setTimeout(() => toastWarning(message, undefined, { autoClose: false }), 800);
    return () => clearTimeout(timer);
  }, [result]);

  /** Drop the remote sources of the active config and restart on its cache. */
  const fallBackToLocal = () => {
    const key = resolveActiveSettingsKey();
    try {
      setSettingsConfigVariablesUrl(key, undefined);
      setSettingsConfigSource(key, { mode: "local", variables: getSettingsConfigVariables(key) });
    } catch (err) { /* nothing better to do than reload anyway */ }
    window.location.reload();
  };

  /** The failure, shown over the running app rather than in its place. */
  const errorOverlay = () => (
    <div className={styles.overlay}>
      <AnimatedBackground contentClassName={styles.container}>
        <div className={styles.panel}>
          <div className={styles.title}>Configuration distante inaccessible</div>
          {result?.url ? <div className={styles.url}>{result.url}</div> : null}
          <div className={styles.error}>{result?.error}</div>
          <div className={styles.hint}>
            {result?.hasCache
              ? `L'application continue sur la dernière configuration chargée (${Math.max(countdown, 0)} s).`
              : `Aucune configuration n'a encore été chargée depuis cette URL : l'application continue sans paramètres (${Math.max(countdown, 0)} s).`}
          </div>
          <div className={styles.buttons}>
            <Button.Simple label="Réessayer" onClick={load} />
            <Button.Simple label="Continuer" onClick={() => setDismissed(true)} />
            <Button.Simple label="Repasser en configuration locale" onClick={fallBackToLocal} />
          </div>
        </div>
      </AnimatedBackground>
    </div>
  );

  if (!result && !proceeded) return <LoadingView fullscreen message="Chargement de la configuration" />;

  // The app is mounted whatever happens — it is what tells the wrapper the page
  // is alive — and a failed load is shown over it rather than in its place.
  return (
    <>
      {children}
      {blocked ? errorOverlay() : null}
    </>
  );
};
